import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

// Rejects any date strictly earlier than today's midnight. Kept as a plain
// function so it can be used in field initializers (arrow-property validators
// on the class would be referenced before their own initialization).
function noPastDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value as Date | null;
  if (!value) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return value.getTime() < today.getTime() ? { pastDate: true } : null;
}

import { FileDropZoneComponent } from '../../../core/shared/file-drop-zone/file-drop-zone.component';
import {
  AnalyzeUploadedTemplateMetadata,
  AnalyzeUploadedTemplateResponse,
  RegisterMasterTemplateMetadata,
  UpdateMasterTemplateMetadata,
} from '../../models/document-asset-analysis.model';
import { SUB_DOMAIN_OPTIONS } from '../../models/document-asset-labels';
import {
  DocumentActivationMode,
  DocumentAssetSummary,
  RegisterAssetMetadata,
  UpdateAssetMetadata,
} from '../../models/document-asset.model';
import { DocumentArticleV2Service } from '../../services/document-article-v2.service';
import { DocumentAttachmentV2Service } from '../../services/document-attachment-v2.service';
import { DocumentTemplateV2Service } from '../../services/document-template-v2.service';

/**
 * Kind of asset the wizard is registering. Drives which V2 service is used
 * and which MIME types are accepted for upload.
 */
export type DocumentAssetKind = 'template' | 'attachment' | 'article';

/**
 * Payload emitted on successful registration – parent components use it to
 * refresh their lists and route to the new detail page.
 */
export interface DocumentAssetRegisteredEvent {
  assetId: number;
  businessKey: string;
  version: number;
  status: string;
  warnings: string[];
}

interface ActivationOption {
  label: string;
  value: DocumentActivationMode;
  description: string;
}

const ACTIVATION_OPTIONS: ActivationOption[] = [
  {
    label: 'Salva come Bozza',
    value: 'DRAFT_ONLY',
    description:
      'Il documento viene registrato ma non attivato. Potrà essere pubblicato in seguito.',
  },
  {
    label: 'Attiva subito',
    value: 'ACTIVATE_IMMEDIATELY',
    description:
      'La nuova versione entra in vigore immediatamente e sostituisce la precedente.',
  },
  {
    label: 'Pianifica attivazione',
    value: 'REGISTER_AND_SCHEDULE',
    description:
      'La nuova versione entra in vigore in una data futura. Compila le date di validità.',
  },
];

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

@Component({
  selector: 'app-document-asset-upload-wizard',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    DatePickerModule,
    CheckboxModule,
    MessageModule,
    TagModule,
    FileDropZoneComponent,
  ],
  templateUrl: './document-asset-upload-wizard.component.html',
  styleUrls: ['./document-asset-upload-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentAssetUploadWizardComponent {
  private readonly fb = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly templateService = inject(DocumentTemplateV2Service);
  private readonly attachmentService = inject(DocumentAttachmentV2Service);
  private readonly articleService = inject(DocumentArticleV2Service);

  /** Two-way visibility flag driven by parent [(visible)]. */
  readonly visible = model<boolean>(false);

  /** Asset type being created. Drives allowed MIME and target endpoint. */
  readonly assetKind = input.required<DocumentAssetKind>();

  /** Domain of the parent list (SUPPLIERS | PROFESSIONALS). */
  readonly domainType = input.required<'SUPPLIERS' | 'PROFESSIONALS'>();

  /** 'create' opens the full 3-step wizard; 'edit' opens the 2-step edit flow. */
  readonly mode = input<'create' | 'edit'>('create');

  /** Existing asset being edited. Required when mode='edit'. */
  readonly existingAsset = input<DocumentAssetSummary | null>(null);

  /** Emitted when the user successfully registers a new asset. */
  readonly registered = output<DocumentAssetRegisteredEvent>();

  @ViewChild(FileDropZoneComponent) private readonly dropZone?: FileDropZoneComponent;

  readonly step = signal<1 | 2 | 3>(1);
  readonly selectedFile = signal<File | null>(null);
  readonly isAnalyzing = signal(false);
  readonly isSubmitting = signal(false);
  readonly analysis = signal<AnalyzeUploadedTemplateResponse | null>(null);
  readonly registrationResult = signal<DocumentAssetRegisteredEvent | null>(null);
  readonly showBusinessKeyOverride = signal(false);

  /**
   * Numeric progressive fetched from the backend when the wizard opens in
   * create mode. Rendered as a zero-padded 3-digit prefix (e.g. `003`) that is
   * prepended to the auto-derived businessKey to avoid collisions between
   * different assets sharing the same human name.
   *
   * `null` = not yet fetched (or edit mode, where the BK is immutable).
   */
  readonly progressivePrefix = signal<string | null>(null);

  readonly subDomainOptions = SUB_DOMAIN_OPTIONS;
  readonly activationOptions = ACTIVATION_OPTIONS;

  readonly acceptedMime = computed<string>(() => {
    switch (this.assetKind()) {
      case 'template':
        return DOCX_MIME;
      case 'attachment':
        return `${DOCX_MIME},application/pdf,text/html`;
      case 'article':
        return `${DOCX_MIME},text/html`;
      default:
        return DOCX_MIME;
    }
  });

  readonly dialogTitle = computed(() => {
    const editing = this.mode() === 'edit';
    switch (this.assetKind()) {
      case 'template':
        return editing ? 'Modifica template documentale' : 'Nuovo template documentale';
      case 'attachment':
        return editing ? 'Modifica allegato riusabile' : 'Nuovo allegato riusabile';
      case 'article':
        return editing ? 'Modifica articolo riusabile' : 'Nuovo articolo riusabile';
    }
  });

  readonly stepLabels = computed<string[]>(() =>
    this.mode() === 'edit'
      ? ['Metadati e file', 'Verifica']
      : ['Upload', 'Metadati', 'Verifica'],
  );

  readonly totalSteps = computed(() => (this.mode() === 'edit' ? 2 : 3));

  readonly isEdit = computed(() => this.mode() === 'edit');

  // Which UI block is currently visible (independent of numeric step / mode).
  readonly isUploadStep = computed(() => !this.isEdit() && this.step() === 1);
  readonly isMetadataStep = computed(
    () => (!this.isEdit() && this.step() === 2) || (this.isEdit() && this.step() === 1),
  );
  readonly isReviewStep = computed(
    () => (!this.isEdit() && this.step() === 3) || (this.isEdit() && this.step() === 2),
  );

  // Original subDomain of the asset being edited — used to detect a change
  // that forces a new version (POST /register instead of PUT metadata).
  private readonly originalSubDomain = signal<string | null>(null);

  // Signal mirror of the subDomainType FormControl value (form controls are
  // not signals, so `computed` cannot depend on them directly).
  private readonly currentSubDomain = signal<string | null>(null);

  readonly subDomainChanged = computed(() => {
    if (!this.isEdit()) return false;
    const original = (this.originalSubDomain() ?? '').toUpperCase();
    const current = (this.currentSubDomain() ?? '').toUpperCase();
    return original !== current;
  });

  readonly requiresNewVersion = computed(
    () => this.isEdit() && (this.selectedFile() !== null || this.subDomainChanged()),
  );

  // Midnight today: the earliest date allowed for a scheduled activation.
  readonly minScheduleDate = this.startOfToday();

  readonly hasBlockingErrors = computed(
    () => (this.analysis()?.blockingErrors?.length ?? 0) > 0,
  );

  readonly form: FormGroup = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    businessKey: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^[A-Z0-9_]+$/)]],
    subDomainType: [null as string | null, Validators.required],
    activationMode: ['ACTIVATE_IMMEDIATELY' as DocumentActivationMode, Validators.required],
    validFrom: [null as Date | null, noPastDateValidator],
    validTo: [null as Date | null, noPastDateValidator],
    changeDescription: ['', Validators.maxLength(2000)],
  });

  // Signal-driven form validity so OnPush re-renders when statusChanges emits.
  readonly formInvalid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map(() => this.form.invalid),
    ),
    { initialValue: this.form.invalid },
  );

  constructor() {
    // Auto-derive businessKey from name when override is off.
    effect(() => {
      const overrideOn = this.showBusinessKeyOverride();
      const nameCtrl = this.form.get('name');
      const bkCtrl = this.form.get('businessKey');
      if (!nameCtrl || !bkCtrl) return;
      if (overrideOn) {
        bkCtrl.enable({ emitEvent: false });
      } else {
        bkCtrl.disable({ emitEvent: false });
      }
    });
    this.form.get('name')?.valueChanges.subscribe((raw: string) => {
      if (this.isEdit()) return;
      if (this.showBusinessKeyOverride()) return;
      const derived = this.deriveBusinessKey(raw ?? '');
      this.form.get('businessKey')?.setValue(derived, { emitEvent: false });
    });
    this.form
      .get('subDomainType')
      ?.valueChanges.subscribe((v: string | null) => {
        this.currentSubDomain.set(v ?? null);
      });
    // Reset internal state when the dialog is closed by the parent.
    effect(() => {
      if (!this.visible()) {
        this.resetWizard();
      }
    });

    // When the dialog opens in edit mode, pre-fill the form from the existing
    // asset and disable businessKey editing.
    effect(() => {
      if (!this.visible()) return;
      if (this.mode() !== 'edit') return;
      const asset = this.existingAsset();
      if (!asset) return;
      this.applyEditPrefill(asset);
    });

    // When the dialog opens in create mode, fetch the next progressive prefix
    // for the current asset kind so that the auto-derived businessKey is
    // globally unique (e.g. `003_MY_TEMPLATE`).
    effect(() => {
      if (!this.visible()) return;
      if (this.mode() !== 'create') return;
      this.fetchProgressivePrefix();
    });
  }

  // ---------------------------------------------------------------------------
  // Step 1 – file selection
  // ---------------------------------------------------------------------------

  onFilesSelected(files: File[]): void {
    const [file] = files;
    if (!file) return;
    this.selectedFile.set(file);
  }

  onFileRemoved(): void {
    this.selectedFile.set(null);
    this.analysis.set(null);
  }

  goToMetadata(): void {
    if (!this.selectedFile()) return;
    this.step.set(2);
  }

  // ---------------------------------------------------------------------------
  // Step 2 – metadata + analyze
  // ---------------------------------------------------------------------------

  backToUpload(): void {
    this.step.set(1);
  }

  runAnalyze(): void {
    const file = this.selectedFile();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // In edit mode without a new file we validate the currently stored file
    // by re-running the analyzer against the S3 copy.
    if (this.isEdit() && !file) {
      const asset = this.existingAsset();
      if (!asset) return;
      this.isAnalyzing.set(true);
      this.analyzeRegisteredService(asset.assetId).subscribe({
        next: (response) => {
          this.isAnalyzing.set(false);
          this.analysis.set(response?.data ?? null);
          this.step.set(2);
        },
        error: (err) => this.handleAnalyzeError(err),
      });
      return;
    }
    if (!file) return;
    this.isAnalyzing.set(true);
    const value = this.form.getRawValue();
    const metadata: AnalyzeUploadedTemplateMetadata = {
      businessKey: value.businessKey,
      name: value.name,
      domainType: this.domainType(),
      subDomainType: value.subDomainType ?? undefined,
      activationMode: value.activationMode,
      validFrom: this.toIsoDate(value.validFrom),
      validTo: this.toIsoDate(value.validTo),
    };

    const analyze$ = this.analyzeService(file, metadata);
    analyze$.subscribe({
      next: (response) => {
        this.isAnalyzing.set(false);
        const data = response?.data ?? null;
        this.analysis.set(data);
        this.step.set(this.isEdit() ? 2 : 3);
      },
      error: (err) => this.handleAnalyzeError(err),
    });
  }

  private handleAnalyzeError(err: unknown): void {
    this.isAnalyzing.set(false);
    console.error('Wizard analyze failed', err);
    this.messageService.add({
      severity: 'error',
      summary: 'Analisi fallita',
      detail:
        (err as { error?: { message?: string } })?.error?.message ??
        'Impossibile analizzare il file. Verifica il formato e riprova.',
    });
  }

  // ---------------------------------------------------------------------------
  // Step 3 – confirm & register
  // ---------------------------------------------------------------------------

  backToMetadata(): void {
    this.step.set(this.isEdit() ? 1 : 2);
  }

  register(): void {
    if (this.form.invalid || this.hasBlockingErrors()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEdit() && !this.requiresNewVersion()) {
      this.submitMetadataUpdate();
      return;
    }

    const file = this.selectedFile();
    if (file) {
      this.submitRegister(file);
      return;
    }

    // Edit + metadata-only change that still requires a new version (e.g. new
    // category): reuse the file currently stored on S3.
    const asset = this.existingAsset();
    if (this.isEdit() && asset) {
      this.reuseExistingFileForNewVersion(asset);
    }
  }

  private reuseExistingFileForNewVersion(asset: DocumentAssetSummary): void {
    const kind = this.assetKind();
    if (kind === 'template') {
      this.messageService.add({
        severity: 'warn',
        summary: 'File richiesto',
        detail:
          'Per i master template la nuova versione richiede il caricamento del file DOCX aggiornato.',
      });
      return;
    }
    this.isSubmitting.set(true);
    const download$ =
      kind === 'attachment'
        ? this.attachmentService.download(asset.assetId)
        : this.articleService.download(asset.assetId);
    download$.subscribe({
      next: (blob) => {
        const filename = asset.originalFileName || `${asset.businessKey}.docx`;
        const file = new File([blob], filename, {
          type: blob.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        this.submitRegister(file);
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.handleRegisterError(err);
      },
    });
  }

  private submitRegister(file: File): void {
    this.isSubmitting.set(true);
    const value = this.form.getRawValue();
    const activationMode: DocumentActivationMode = value.activationMode;

    const kind = this.assetKind();
    if (kind === 'template') {
      const metadata: RegisterMasterTemplateMetadata = {
        businessKey: value.businessKey,
        name: value.name,
        domainType: this.domainType(),
        subDomainType: value.subDomainType ?? undefined,
        activationMode,
        validFrom: this.toIsoDate(value.validFrom),
        validTo: this.toIsoDate(value.validTo),
        autoCreateBindings: true,
        changeDescription: value.changeDescription || undefined,
      };
      this.templateService.registerMaster(file, metadata).subscribe({
        next: (res) => this.handleRegisterSuccess(res?.data),
        error: (err) => this.handleRegisterError(err),
      });
    } else {
      const metadata: RegisterAssetMetadata = {
        businessKey: value.businessKey,
        name: value.name,
        domainType: this.domainType(),
        subDomainType: value.subDomainType ?? undefined,
        activationMode,
        validFrom: this.toIsoDate(value.validFrom),
        validTo: this.toIsoDate(value.validTo),
        changeDescription: value.changeDescription || undefined,
      };
      const call$ =
        kind === 'attachment'
          ? this.attachmentService.register(file, metadata)
          : this.articleService.register(file, metadata);
      call$.subscribe({
        next: (res) => this.handleRegisterSuccess(res?.data),
        error: (err) => this.handleRegisterError(err),
      });
    }
  }

  private submitMetadataUpdate(): void {
    const asset = this.existingAsset();
    if (!asset) return;
    this.isSubmitting.set(true);
    const value = this.form.getRawValue();
    const commonPayload = {
      name: value.name,
      validFrom: this.toIsoDate(value.validFrom),
      validTo: this.toIsoDate(value.validTo),
      changeDescription: value.changeDescription || undefined,
    };

    // Each service returns a different response envelope shape. Normalize each
    // branch individually to a plain summary so the outer Observable has a
    // single, callable signature.
    type Summary = {
      assetId: number;
      businessKey: string;
      version: number;
      status: string;
    };
    const toSummary = (res: {
      data?: { assetId: number; businessKey: string; version: number; status?: string };
    }): Summary | null => {
      const d = res?.data;
      return d
        ? {
            assetId: d.assetId,
            businessKey: d.businessKey,
            version: d.version,
            status: d.status ?? asset.status ?? 'ACTIVE',
          }
        : null;
    };

    const kind = this.assetKind();
    const normalized$: Observable<Summary | null> =
      kind === 'template'
        ? this.templateService
            .update(asset.assetId, commonPayload as UpdateMasterTemplateMetadata)
            .pipe(map(toSummary))
        : kind === 'attachment'
          ? this.attachmentService
              .update(asset.assetId, commonPayload as UpdateAssetMetadata)
              .pipe(map(toSummary))
          : this.articleService
              .update(asset.assetId, commonPayload as UpdateAssetMetadata)
              .pipe(map(toSummary));

    normalized$.subscribe({
      next: (event) => this.handleRegisterSuccess(event ?? undefined),
      error: (err: unknown) => this.handleRegisterError(err),
    });
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  toggleBusinessKeyOverride(): void {
    this.showBusinessKeyOverride.update((prev) => !prev);
  }

  private analyzeService(file: File, metadata: AnalyzeUploadedTemplateMetadata) {
    switch (this.assetKind()) {
      case 'template':
        return this.templateService.analyzeUpload(file, metadata);
      case 'attachment':
        return this.attachmentService.analyzeUpload(file, metadata);
      case 'article':
        return this.articleService.analyzeUpload(file, metadata);
    }
  }

  private analyzeRegisteredService(assetId: number) {
    switch (this.assetKind()) {
      case 'template':
        return this.templateService.analyzeRegistered(assetId);
      case 'attachment':
        return this.attachmentService.analyzeRegistered(assetId);
      case 'article':
        return this.articleService.analyzeRegistered(assetId);
    }
  }

  private applyEditPrefill(asset: DocumentAssetSummary): void {
    this.originalSubDomain.set(asset.subDomainType ?? null);
    this.currentSubDomain.set(asset.subDomainType ?? null);
    this.form.reset({
      name: asset.name ?? '',
      businessKey: asset.businessKey,
      subDomainType: asset.subDomainType ?? null,
      activationMode: 'ACTIVATE_IMMEDIATELY',
      validFrom: asset.validFrom ? new Date(asset.validFrom) : null,
      validTo: asset.validTo ? new Date(asset.validTo) : null,
      changeDescription: '',
    });
    // businessKey is never editable in edit mode.
    this.form.get('businessKey')?.disable({ emitEvent: false });
    this.showBusinessKeyOverride.set(false);
    this.selectedFile.set(null);
    this.analysis.set(null);
    this.step.set(1);
  }

  private handleRegisterSuccess(
    data:
      | { assetId: number; businessKey: string; version: number; status: string; warnings?: string[] }
      | DocumentAssetSummary
      | null
      | undefined,
  ): void {
    this.isSubmitting.set(false);
    if (!data) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Registrazione non riuscita: risposta vuota dal server.',
      });
      return;
    }
    const event: DocumentAssetRegisteredEvent = {
      assetId: data.assetId,
      businessKey: data.businessKey,
      version: data.version,
      status: data.status ?? 'DRAFT',
      warnings: 'warnings' in data && Array.isArray(data.warnings) ? data.warnings : [],
    };
    this.registrationResult.set(event);
    this.messageService.add({
      severity: 'success',
      summary: 'Registrazione riuscita',
      detail: `${data.businessKey} v${data.version} — ${this.humanStatus(event.status)}.`,
      life: 4000,
    });
    this.registered.emit(event);
    this.visible.set(false);
  }

  private handleRegisterError(err: unknown): void {
    this.isSubmitting.set(false);
    console.error('Wizard register failed', err);
    const payload = (err as { error?: { message?: string; traceId?: string } })?.error;
    const detail = payload?.message ?? 'Impossibile completare l\u2019operazione. Riprova o contatta il supporto.';
    const summary = payload?.traceId
      ? `Registrazione fallita (rif. ${payload.traceId})`
      : 'Registrazione fallita';
    this.messageService.add({
      severity: 'error',
      summary,
      detail,
    });
  }

  private resetWizard(): void {
    this.step.set(1);
    this.selectedFile.set(null);
    this.analysis.set(null);
    this.registrationResult.set(null);
    this.showBusinessKeyOverride.set(false);
    this.progressivePrefix.set(null);
    this.originalSubDomain.set(null);
    this.currentSubDomain.set(null);
    this.form.reset({
      name: '',
      businessKey: '',
      subDomainType: null,
      activationMode: 'ACTIVATE_IMMEDIATELY',
      validFrom: null,
      validTo: null,
      changeDescription: '',
    });
    this.dropZone?.reset();
  }

  private deriveBusinessKey(raw: string): string {
    const slug = raw
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const prefix = this.progressivePrefix();
    if (!prefix || !slug) return slug;
    return `${prefix}_${slug}`;
  }

  private fetchProgressivePrefix(): void {
    const kind = this.assetKind();
    const svc$ =
      kind === 'template'
        ? this.templateService.getNextProgressive()
        : kind === 'attachment'
          ? this.attachmentService.getNextProgressive()
          : this.articleService.getNextProgressive();
    svc$.subscribe({
      next: (res) => {
        const n = res?.data;
        if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
          this.progressivePrefix.set(String(n).padStart(3, '0'));
          // Recompute BK from current name so the prefix is applied immediately
          if (!this.showBusinessKeyOverride()) {
            const nameCtrl = this.form.get('name');
            const bkCtrl = this.form.get('businessKey');
            const raw = (nameCtrl?.value ?? '') as string;
            bkCtrl?.setValue(this.deriveBusinessKey(raw), { emitEvent: false });
          }
        }
      },
      error: (err) => {
        console.error('Failed to fetch next businessKey progressive', err);
        // Silent fallback: keep the previous (or null) prefix; the user can
        // still override the BK manually if needed.
      },
    });
  }

  private toIsoDate(value: Date | null | undefined): string | undefined {
    if (!value) return undefined;
    const y = value.getFullYear();
    const m = `${value.getMonth() + 1}`.padStart(2, '0');
    const d = `${value.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private humanStatus(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'attivo';
      case 'DRAFT':
        return 'bozza';
      case 'RETIRED':
        return 'ritirato';
      default:
        return status.toLowerCase();
    }
  }
}
